import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useId, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleUp, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import * as Colors from '@/utils/helpers/ColorCodes';
import { ColorWheelSVG } from '../FilterSVGs';
import type { ColorFilterProps } from '@/types/searchSide';
interface ColorButtonsProps {
  values: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
}
const ColorButtons: React.FC<ColorButtonsProps> = t0 => {
  const $ = _c(9);
  const {
    values,
    selectedValues,
    onChange
  } = t0;
  let t1;
  if ($[0] !== onChange || $[1] !== selectedValues) {
    t1 = color => {
      const isSelected = selectedValues.includes(color);
      if (isSelected) {
        onChange(selectedValues.filter(item => item !== color));
      } else {
        onChange([...selectedValues, color]);
      }
    };
    $[0] = onChange;
    $[1] = selectedValues;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleColorSelection = t1;
  let t2;
  if ($[3] !== handleColorSelection || $[4] !== selectedValues || $[5] !== values) {
    t2 = values?.map((value, index) => <button key={index} type="button" className={`t-flex t-items-center t-gap-2.5 t-rounded-md t-px-1 t-py-2 t-text-left t-text-[13px] t-font-medium t-transition-colors t-duration-200 ${selectedValues.includes(value) ? "t-bg-[#f7f3ff] t-text-brand-violet-dark" : "t-bg-white t-text-[#5e5864] hover:t-bg-[#faf9fb]"}`} onClick={e => {
      handleColorSelection(value);
      e.stopPropagation();
    }} aria-pressed={selectedValues.includes(value)} aria-label={`${value} color ${selectedValues.includes(value) ? "selected" : "not selected"}`} onKeyDown={e_0 => {
      if (e_0.key === "Enter" || e_0.key === " ") {
        e_0.preventDefault();
        handleColorSelection(value);
      }
    }}>{value !== "Other" && <div className="t-h-4 t-w-4 t-flex-shrink-0 t-rounded-full t-border t-border-[#cfcad2]" style={{
        backgroundColor: Colors.ReturnColorCode(Colors, value) ? Colors.ReturnColorCode(Colors, value) : "black"
      }} aria-hidden="true" />}{value === "Other" && <div className="t-flex t-h-4 t-w-4 t-items-center t-justify-center"><ColorWheelSVG /></div>}<span className="t-truncate">{value}</span></button>);
    $[3] = handleColorSelection;
    $[4] = selectedValues;
    $[5] = values;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <div className="t-grid t-grid-cols-2 t-gap-1">{t2}</div>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const ClearButton: React.FC<{
  onClear: () => void;
  count: number;
}> = t0 => {
  const $ = _c(7);
  const {
    onClear,
    count
  } = t0;
  const t1 = `Clear ${count} selected colors`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <svg className="t-w-3.5 t-h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== count) {
    t3 = <span>Clear {count}</span>;
    $[1] = count;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onClear || $[4] !== t1 || $[5] !== t3) {
    t4 = <button type="button" className="t-inline-flex t-items-center t-gap-1 t-text-xs t-font-semibold t-text-automotive-primary hover:t-text-automotive-secondary t-transition-colors focus:t-outline-none focus:t-ring-2 focus:t-ring-automotive-primary focus:t-ring-offset-2 t-rounded" onClick={onClear} aria-label={t1}>{t2}{t3}</button>;
    $[3] = onClear;
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
export const ColorFilter: React.FC<ColorFilterProps & {
  isOpen: boolean;
  onToggle: () => void;
  screen?: string;
}> = t0 => {
  const $ = _c(39);
  const {
    interiorColors,
    exteriorColors,
    interiorOptions,
    exteriorOptions,
    onInteriorColorChange,
    onExteriorColorChange,
    onClear,
    isOpen,
    onToggle,
    className: t1,
    "aria-label": ariaLabel
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const [currentTab, setCurrentTab] = useState(0);
  const sectionId = useId();
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== ariaLabel || $[1] !== className || $[2] !== currentTab || $[3] !== exteriorColors || $[4] !== exteriorOptions || $[5] !== interiorColors || $[6] !== interiorOptions || $[7] !== isOpen || $[8] !== onClear || $[9] !== onExteriorColorChange || $[10] !== onInteriorColorChange || $[11] !== onToggle || $[12] !== sectionId) {
    const tabs = ["Exterior", "Interior"];
    const triggerId = `color-${sectionId}-trigger`;
    const contentId = `color-${sectionId}-content`;
    const totalSelectedColors = interiorColors.length + exteriorColors.length;
    const handleTabClick = tabNumber => {
      if (currentTab !== tabNumber) {
        setCurrentTab(tabNumber);
      }
    };
    t4 = className;
    t5 = ariaLabel;
    let t6;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <span>Color</span>;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    const t7 = isOpen ? faAngleUp : faAngleDown;
    let t8;
    if ($[18] !== t7) {
      t8 = <FontAwesomeIcon icon={t7} className="t-h-4 t-w-4 t-text-[#928c96]" aria-hidden="true" />;
      $[18] = t7;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    let t9;
    if ($[20] !== contentId || $[21] !== isOpen || $[22] !== onToggle || $[23] !== t8 || $[24] !== triggerId) {
      t9 = <button id={triggerId} className="t-flex t-w-full t-items-center t-justify-between t-text-left t-text-sm t-font-extrabold t-text-[#38323d] t-transition-colors hover:t-text-brand-violet focus:t-rounded focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-violet focus:t-ring-offset-2" type="button" onClick={onToggle} aria-expanded={isOpen} aria-controls={contentId}>{t6}{t8}</button>;
      $[20] = contentId;
      $[21] = isOpen;
      $[22] = onToggle;
      $[23] = t8;
      $[24] = triggerId;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    let t10;
    if ($[26] !== onClear || $[27] !== totalSelectedColors) {
      t10 = totalSelectedColors > 0 && <div className="t-mt-2"><ClearButton onClear={onClear} count={totalSelectedColors} /></div>;
      $[26] = onClear;
      $[27] = totalSelectedColors;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    if ($[29] !== t10 || $[30] !== t9) {
      t2 = <div>{t9}{t10}</div>;
      $[29] = t10;
      $[30] = t9;
      $[31] = t2;
    } else {
      t2 = $[31];
    }
    t3 = isOpen && <div id={contentId} className="t-mt-4" role="group" aria-labelledby={triggerId}><div className="t-flex t-border-b t-border-gray-200 t-mb-4" role="tablist" aria-label="Color filter tabs" aria-orientation="horizontal">{tabs.map((tab, index) => <button key={index} type="button" className={`t-flex-1 t-px-4 t-py-2 t-text-sm t-font-medium t-transition-colors t-duration-200 t-border-b-2 ${currentTab === index ? "t-border-brand-violet t-text-brand-violet" : "t-border-transparent t-text-gray-600 hover:t-text-gray-800 hover:t-border-gray-300"}`} onClick={() => handleTabClick(index)} role="tab" tabIndex={currentTab === index ? 0 : -1} aria-selected={currentTab === index} aria-controls={`${tab.toLowerCase()}-${sectionId}-color-panel`} id={`${tab.toLowerCase()}-${sectionId}-color-tab`}>{tab}</button>)}</div><div className="t-space-y-4">{tabs.map((tab_0, index_0) => currentTab === index_0 ? <div key={tab_0} role="tabpanel" id={`${tab_0.toLowerCase()}-${sectionId}-color-panel`} aria-labelledby={`${tab_0.toLowerCase()}-${sectionId}-color-tab`} tabIndex={0}><ColorButtons values={currentTab === 0 ? exteriorOptions : interiorOptions} selectedValues={currentTab === 0 ? exteriorColors : interiorColors} onChange={currentTab === 0 ? onExteriorColorChange : onInteriorColorChange} /></div> : null)}</div></div>;
    $[0] = ariaLabel;
    $[1] = className;
    $[2] = currentTab;
    $[3] = exteriorColors;
    $[4] = exteriorOptions;
    $[5] = interiorColors;
    $[6] = interiorOptions;
    $[7] = isOpen;
    $[8] = onClear;
    $[9] = onExteriorColorChange;
    $[10] = onInteriorColorChange;
    $[11] = onToggle;
    $[12] = sectionId;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
  }
  let t6;
  if ($[32] !== t2 || $[33] !== t3) {
    t6 = <div>{t2}{t3}</div>;
    $[32] = t2;
    $[33] = t3;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== t4 || $[36] !== t5 || $[37] !== t6) {
    t7 = <div className={t4} aria-label={t5}>{t6}</div>;
    $[35] = t4;
    $[36] = t5;
    $[37] = t6;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  return t7;
};